import { Box, Grid, Title } from '@mantine/core';
import { IconPointer } from '@tabler/icons-react';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import queryString from 'query-string';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import ErrorMessage from '../../components/ErrorMessage';
import EventSelector from '../../components/EventSelector';
import Pager from '../../components/Pager/Pager';
import ProfileEvents from '../../components/Profiles/ProfileEvents';
import Spinner from '../../components/Spinner';
import PageSizeOptions from '../../constants/pageSizeOptions';
import useEventTypes from '../../helpers/useEventTypes';
import useFetch from '../../helpers/useFetch';

const fetchDebounced = AwesomeDebouncePromise(
  (fetch, accountId, domainId, eventFilter, trackingId) =>
    fetch(
      `/api/admin/accounts/${accountId}/domains/${domainId}/profiles/events?id=${encodeURIComponent(
        trackingId
      )}&${queryString.stringify(eventFilter, { arrayFormat: 'bracket' })}`
    ),
  300
);

const filterDefaultValues = { from: 0, size: PageSizeOptions[0] };

const ProfilesDetailsEventsPage = ({ trackingId }) => {
  const fetch = useFetch();
  const location = useLocation();
  const navigate = useNavigate();

  const parsedSearch = useMemo(() => {
    return queryString.parse(location.search, { arrayFormat: 'bracket' });
  }, [location.search]);

  const [events, setEvents] = useState();
  const [eventFilter, setEventFilter] = useState(filterDefaultValues);
  const [totalCount, setTotalCount] = useState();
  const [pageCount, setPageCount] = useState();
  const [error, setError] = useState();
  const { accountId, domainId } = useParams();
  const eventTypes = useEventTypes(accountId, domainId, setError);

  useEffect(() => {
    const newFilter = { ...filterDefaultValues, ...parsedSearch };
    setEventFilter(newFilter);
    setEvents(undefined);
    fetchDebounced(fetch, accountId, domainId, parsedSearch, trackingId)
      .then(({ events, totalCount }) => {
        setTotalCount(totalCount);
        setPageCount(Math.ceil(totalCount / eventFilter.size));
        setEvents(events);
      })
      .catch(setError);
  }, [accountId, domainId, fetch, trackingId, parsedSearch, eventFilter.size]);

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  const handlePageClick = (e) => {
    const selectedPage = e.selected;
    const newOffset = (selectedPage - 1) * eventFilter.size;
    if (newOffset !== parsedSearch.from) {
      navigate({
        search: queryString.stringify(
          { ...parsedSearch, from: (selectedPage - 1) * parseInt(eventFilter.size) },
          { arrayFormat: 'bracket' }
        ),
      });
    }
  };

  const setPageSize = async (pageSize) => {
    navigate({ search: queryString.stringify({ ...parsedSearch, size: pageSize }, { arrayFormat: 'bracket' }) });
  };

  const onSelectedFiltersChange = (values) => {
    navigate({
      search: queryString.stringify(
        {
          ...queryString.parse(location.search, { arrayFormat: 'bracket' }),
          eventFilter: values,
          from: 0,
        },
        { arrayFormat: 'bracket' }
      ),
    });
  };

  if (!events || !eventTypes) {
    return <Spinner />;
  }

  return (
    <div data-tour="profileDetailsEvents">
      <Grid mb="sm">
        <Grid.Col span={{ base: 12, md: 2 }} />
        <Grid.Col span={{ base: 12, md: 8 }} data-tour="profilesDetailsEventsFilter">
          <EventSelector
            isMulti={true}
            actions={parsedSearch?.eventFilter || []}
            setActions={onSelectedFiltersChange}
          />
        </Grid.Col>
      </Grid>

      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }} ta="left">
          <Box style={{ float: 'left' }}>
            <Title order={5}>
              <IconPointer size={16} /> {totalCount.toLocaleString(navigator.language)} Events
            </Title>
          </Box>
        </Grid.Col>
      </Grid>

      <ProfileEvents events={events} eventTypes={eventTypes} />
      <Pager
        pageSize={parseInt(eventFilter.size)}
        pageCount={pageCount}
        onPageChange={handlePageClick.bind(this)}
        initialPage={Math.round(parseInt(parsedSearch.from || '0') / parseInt(eventFilter.size) + 1)}
        setPageSize={setPageSize}
      />
    </div>
  );
};

export default ProfilesDetailsEventsPage;
